import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
    Box,
    Button,
    Grid,
    MenuItem,
    Select,
    FormControl,
    CircularProgress,
    Snackbar,
    Chip,
    Tooltip,
    IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MuiAlert from "@material-ui/lab/Alert";
import axios from "../../../../axios-order";
import BoxWrapper from "../../../../components/ui/boxWrapper";
import DownloadIcon from "@mui/icons-material/Download";
import Modal from "react-bootstrap/Modal";
import { ValidatorForm } from "react-material-ui-form-validator";
import DatePicker from './DatePicker';
import moment from "moment";
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const GSTRepositoryMain = () => {
    const history = useHistory();
    const [open, setOpen] = useState(false);
    const [modalShow, setModalShow] = useState(false);
    const [gstType, setGstType] = useState([]);
    const [financialYear, setFinancialYear] = useState("");
    const [fromMonth, setFromMonth] = useState("");
    const [toMonth, setToMonth] = useState("");
    const [search, setSearch] = useState("");
    const [tableData, setTableData] = useState([]);
    const [originalTableData, setOriginalTableData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [fromMonthYearDisplay, setFromMonthYearDisplay] = useState('');
    const [fromToYearDisplay, setFromToYearDisplay] = useState('');
    const [toMonthError, setToMonthError] = useState('');
    const [reportStatusModalOpen, setReportStatusModalOpen] = useState(false);
    const [autoDownloadStatusData, setAutoDownloadStatusData] = useState(null);
    const monthArr = [
        { id: 4, value: "Apr" },
        { id: 5, value: "May" },
        { id: 6, value: "Jun" },
        { id: 7, value: "Jul" },
        { id: 8, value: "Aug" },
        { id: 9, value: "Sep" },
        { id: 10, value: "Oct" },
        { id: 11, value: "Nov" },
        { id: 12, value: "Dec" },
        { id: 1, value: "Jan" },
        { id: 2, value: "Feb" },
        { id: 3, value: "Mar" },
    ];
    const [toMonthArr, setToMonthArr] = useState(monthArr);

    const handleSnackbarOpen = (message, severity) => {
        setSnackbarOpen(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const closeReportStatusModal = () => {
        setReportStatusModalOpen(false);
        setAutoDownloadStatusData(null);
    };

    const handleSettingsClick = (item) => {
        const business_entity_id = localStorage.getItem("business_entity_id");
        const summary_id = item.id;

        axios.get(`/get_integrated_repo_summary_by_id/${business_entity_id}/${summary_id}`)
            .then((res) => {
                if (res?.data?.status && res?.data?.data?.period_wise_status) {
                    setAutoDownloadStatusData(res.data.data.period_wise_status);
                } else {
                    setAutoDownloadStatusData(null);
                }
                setReportStatusModalOpen(true);
            })
            .catch((error) => {
                console.error("Error fetching settings data:", error);
                setAutoDownloadStatusData(null);
                setReportStatusModalOpen(true);
            });
    };

    const yearArr = (() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear - 7; i <= currentYear + 5; i++) {
            const endYear = (i + 1).toString().slice(-2);
            years.push({ id: `${i}-${endYear}`, value: `${i}-${endYear}` });
        }
        return years;
    })();

    const formRef = useRef();

    useEffect(() => {
        fetchTableData();
    }, []);

    const isWithinFinancialYear = (fromDate, toDate) => {
        if (!fromDate || !toDate) return true;
        const from = moment(fromDate, 'MMYYYY');
        const to = moment(toDate, 'MMYYYY');
        const fromMonth = from.month();
        const fromYear = from.year();
        let fyStart, fyEnd;
        if (fromMonth >= 3) {
            fyStart = moment(`04${fromYear}`, 'MMYYYY');
            fyEnd = moment(`03${fromYear + 1}`, 'MMYYYY');
        } else {
            fyStart = moment(`04${fromYear - 1}`, 'MMYYYY');
            fyEnd = moment(`03${fromYear}`, 'MMYYYY');
        }
        return to.isSameOrAfter(fyStart) && to.isSameOrBefore(fyEnd);
    };

    const getToMonthDateRange = () => {
        if (!fromMonthYearDisplay) return {};
        const from = moment(fromMonthYearDisplay, 'MMYYYY');
        const fromMonth = from.month();
        const fromYear = from.year();
        let fyEnd;
        if (fromMonth >= 3) {
            fyEnd = moment(`03${fromYear + 1}`, 'MMYYYY');
        } else {
            fyEnd = moment(`03${fromYear}`, 'MMYYYY');
        }
        return {
            minDate: from.toDate(),
            maxDate: fyEnd.clone().endOf('month').toDate()
        };
    };

    const inputHandlerFromPicker = (v) => {
        const formattedDate = v ? moment(v).format('MMYYYY') : '';
        setFromMonthYearDisplay(formattedDate);
        setFromToYearDisplay('');
        setToMonthError('');
    };

    const inputHandlerToPicker = (v) => {
        const formattedDate = v ? moment(v).format('MMYYYY') : '';
        setFromToYearDisplay(formattedDate);
        if (fromMonthYearDisplay && formattedDate) {
            if (!isWithinFinancialYear(fromMonthYearDisplay, formattedDate)) {
                setToMonthError("Please select within the financial year (Apr to Mar)");
            } else {
                setToMonthError('');
            }
        }
    };

    const fetchTableData = () => {
        const business_entity_id = localStorage.getItem("business_entity_id");
        axios.get(`/auto_download_for_all_repo_log_summary/${business_entity_id}`)
            .then(response => {
                if (response.data && Array.isArray(response.data.data)) {
                    setTableData(response.data.data);
                    setOriginalTableData(response.data.data);
                } else {
                    setTableData([]);
                    setOriginalTableData([]);
                }
            })
            .catch(error => {
                console.error("Error fetching table data:", error);
                setTableData([]);
                setOriginalTableData([]);
            });
    };

    const handleBackNavigation = () => {
        history.goBack();
    };

    const handleClickOpen = () => {
        setModalShow(true);
    };

    const handleClose = () => {
        setModalShow(false);
        setOpen(false);
        setGstType([]);
        setFromMonthYearDisplay('');
        setFromToYearDisplay('');
        setToMonthError('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // if (!gstType.length || !financialYear || !fromMonth || !toMonth) {
        //     setSnackbarOpen(true);
        //     setSnackbarMessage("All fields are required.");
        //     setSnackbarSeverity("error");
        //     return;
        // }
        setLoading(true);
        setModalShow(false);
        const gstin = localStorage.getItem('gst');
        const business_entity_id = localStorage.getItem("business_entity_id");
        const payLoadData = {
            business_entity_id,
            fromRetPeriod: fromMonthYearDisplay,
            toRetPeriod: fromToYearDisplay,
            gstin: gstin,
            selectedGSTTypes: gstType,
        };

        axios.post(`/auto_download_for_all_repo`, payLoadData)
            .then(response => {
                console.log("Submit Response:", response.data);
                if (response.data) {
                    if (response.data.download_summary_id) {
                        localStorage.setItem("download_summary_id", response.data.download_summary_id);
                    }
                    if (response.data.status === true) {
                        setSnackbarOpen(true);
                        setSnackbarMessage(response.data.message || "GST Repository request submitted successfully.");
                        setSnackbarSeverity("success");
                        return fetchTableData();
                    } else {
                        setSnackbarOpen(true);
                        setSnackbarMessage(response.data.message || "GST Repository request failed.");
                        setSnackbarSeverity("error");
                        return fetchTableData();
                    }
                } else {
                    setSnackbarOpen(true);
                    setSnackbarMessage("Unexpected response format.");
                    setSnackbarSeverity("error");
                    return fetchTableData();
                }
            })
            .catch(error => {
                console.error("Error submitting request:", {
                    message: error.message,
                    response: error?.response?.data,
                    status: error?.response?.status,
                });
                setSnackbarOpen(true);
                setSnackbarMessage(`Error: ${error?.response?.data?.message || "Request failed."}`);
                setSnackbarSeverity("error");
                return fetchTableData();
            })
            .finally(() => {
                setLoading(false);
                setGstType([]);
                setFinancialYear("");
                setFromMonth("");
                setToMonth("");
            });
    };

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearch(query);
        const filteredData = originalTableData.filter(
            (item) =>
                (item.fiscal_year || "").toLowerCase().includes(query) ||
                (item.from_month || "").toLowerCase().includes(query) ||
                (item.to_month || "").toLowerCase().includes(query) ||
                (item.recon_type || "").toLowerCase().includes(query) ||
                (item.status || "").toLowerCase().includes(query)
        );
        setTableData(filteredData);
        console.log("Filtered Data:", filteredData);
    };

    const handleRefreshStatus = (row) => {
        setLoading(true);
        const business_entity_id = localStorage.getItem("business_entity_id");

        const payload = {
            entity_id: business_entity_id,
            download_summary_id: row.id,
            gstr1: row.gstr1_task_id || null,
            gstr2a: row.gstr2a_task_id || null,
            gstr2b: row.gstr2b_task_id || null,
        };

        axios.post(`/auto_download_for_all_repo_refresh`, payload)
            .then(response => {
                if (response.data.status === true) {
                    setSnackbarOpen(true);
                    setSnackbarMessage(response.data.message || "Status refreshed successfully");
                    setSnackbarSeverity("success");
                    fetchTableData();
                } else {
                    setSnackbarOpen(true);
                    setSnackbarMessage(response.data.message || "Failed to refresh status");
                    setSnackbarSeverity("error");
                    fetchTableData();
                }
            })
            .catch(error => {
                console.error("Error refreshing status:", error);
                setSnackbarOpen(true);
                setSnackbarMessage(error.data?.message || "Error refreshing status");
                setSnackbarSeverity("error");
                fetchTableData();
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDownload = (row) => {
        const taskIds = [row.gstr2b_task_id, row.gstr2a_task_id, row.gstr1_task_id];
        const validTaskIds = taskIds.filter(id => id !== null);

        if (validTaskIds.length > 0) {
            axios.get(`/autodownloadapi_for_all/${validTaskIds}`)
                .then(response => {
                    console.log(response.data);
                    if (response.data.status === true) {
                        setSnackbarOpen(true);
                        setSnackbarMessage(response.data.message || "Download successful");
                        setSnackbarSeverity("success");
                    } else {
                        setSnackbarOpen(true);
                        setSnackbarMessage(response.data.message || "Failed to download");
                        setSnackbarSeverity("error");
                    }
                    fetchTableData();
                })
                .catch(error => {
                    console.error('Error making API call:', error);
                    setSnackbarOpen(true);
                    setSnackbarMessage("Error making API call");
                    setSnackbarSeverity("error");
                    fetchTableData();
                });
        } else {
            console.log('No valid task IDs to send.');
            setSnackbarOpen(true);
            setSnackbarMessage("No valid task IDs to send");
            setSnackbarSeverity("error");
        }
    };

    // const handleFromMonthChange = (e) => {
    //     const value = e.target.value;
    //     setFromMonth(value);
    //     setToMonthArr(monthArr.filter(
    //         (month) =>
    //             (parseInt(value) < 4 &&
    //                 parseInt(month.id) >= parseInt(value) &&
    //                 parseInt(month.id) < 4) ||
    //             (parseInt(value) >= 4 && parseInt(month.id) >= parseInt(value)) ||
    //             (parseInt(value) >= 4 && parseInt(month.id) < 4)
    //     ));
    //     setToMonth("");
    // };
    const handleFromMonthChange = (e) => {
        const value = e.target.value;
        setFromMonth(value);
        setToMonthArr(monthArr);
        setToMonth("");
    };

    const handleGstTypeChange = (e) => {
        setGstType(e.target.value);
    };

    const handleFinancialYearChange = (e) => {
        setFinancialYear(e.target.value);
    };

    const handleToMonthChange = (e) => {
        setToMonth(e.target.value);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const tableHeader = () => {
        const tableHeader = [
            "FISCAL YEAR",
            "FROM MONTH",
            "TO MONTH",
            "REPO TYPE",
            "STATUS",
            "REPO RUN DATE TIME",
            "GST REPORT",
            "ACTION",
        ];
        return tableHeader.map((key, index) => {
            return <th key={index}>{key}</th>;
        });
    };

    const tableDataRows = () => {
        if (tableData.length >= 1) {
            return tableData.map((item, index) => (
                <tr key={index}>
                    <td>{item.financial_year}</td>
                    <td>{item.from_month}</td>
                    <td>{item.to_month}</td>
                    <td>{item.recon_type}</td>
                    <td>{item.status}</td>
                    <td>{item.recon_run_date_time}</td>

                    {/* GST Report Column */}
                    <td>
                        {(item.status?.toLowerCase() === "success" || item.status?.toLowerCase() === "partial success") && (
                            <div style={{
                                display: "flex",
                                gap: "1px",
                                alignItems: "center",
                                flexWrap: "nowrap",
                                width: "100%",
                            }}>
                                {item.recon_type.includes("GSTR-1") && (
                                    <Chip
                                        label="1"
                                        size="small"
                                        onClick={() => history.push("/GSTRepo")}
                                        style={{
                                            cursor: "pointer",
                                            fontSize: "10px",
                                            height: "24px",
                                            minWidth: "55px",
                                            padding: "0 2px",
                                            margin: "0 1px",
                                            backgroundColor: "var(--app-bar-bg)",
                                            color: "#FFFF",
                            }}
                                    />
                                )}
                                {item.recon_type.includes("GSTR-2A") && (
                                    <Chip
                                        label="2A"
                                        size="small"
                                        onClick={() => history.push("/Gst2aRepository")}
                                        style={{
                                            cursor: "pointer",
                                            fontSize: "10px",
                                            height: "24px",
                                            minWidth: "60px",
                                            padding: "0 2px",
                                            margin: "0 1px",
                                            backgroundColor: "var(--app-bar-bg)",
                                            color: "#FFFF",
                                }}
                            />
                                )}
                                {item.recon_type.includes("GSTR-2B") && (
                                    <Chip
                                        label="2B"
                                        size="small"
                                        onClick={() => history.push("/Gstr2bRepository")}
                                        style={{
                                            cursor: "pointer",
                                            fontSize: "10px",
                                            height: "24px",
                                            minWidth: "60px",
                                            padding: "0 2px",
                                            margin: "0 1px",
                                            backgroundColor: "var(--app-bar-bg)",
                                            color: "#FFFF",
                            }}
                                    />
                                )}
                            </div>
                        )}
                    </td>

                    {/* Action Column */}
                    <td>
                        <SettingsIcon
                            className="action-icon-button showIcon"
                            onClick={() => handleSettingsClick(item)}
                            style={{
                                cursor: 'pointer',
                                color: 'var(--app-bar-bg)',
                                }}
                            />
                    </td>
                </tr>
            ));
        } else {
            return (
                <tr>
                    <td style={{ color: "red" }} colSpan={7}>
                        No data found!
                    </td>
                </tr>
            );
        }
    };

    return (
        <Box>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Tooltip title="Back" arrow>
                    <IconButton
                        onClick={handleBackNavigation}
                        sx={{
                            borderRadius: '50%',
                            padding: '6px',
                            color: '#263238',
                            fontSize: '20px',
                            backgroundColor: '#ffffff',
                            height: '36px',
                            width: '36px',
                            '&:hover': {
                                backgroundColor: '#455a64',
                                color: '#ffffff',
                            },
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                            transition: 'background-color 0.3s ease',
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </Tooltip>

                {loading ? (
                    <button className="button-outlined-primary" disabled>
                        <CircularProgress size={22} />
                        <span
                            style={{
                                textTransform: "capitalize",
                                paddingLeft: "10px",
                            }}
                        >
                            Uploading...
                        </span>
                    </button>
                ) : (
                    <button
                        className="button-outlined-primary"
                        onClick={handleClickOpen}
                    >
                        New GST Repository
                    </button>
                    
                )}

                <Modal
                    size="lg"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered
                    show={modalShow}
                    onHide={handleClose}
                    dialogClassName="modal-50w"
                >
                    <Modal.Header closeButton className="modal-header">
                        <Modal.Title className="modal-title">
                            New GST Repository
                        </Modal.Title>
                    </Modal.Header>

                    <ValidatorForm
                        ref={formRef}
                        onSubmit={handleSubmit}
                        onError={(errors) => console.log(errors)}
                    ><Modal.Body>
                            <div style={{ padding: "0px 20px 20px 20px" }}>
                                <Grid container spacing={3}>
                                    <Grid item sm={3}>
                                        <p style={{ fontSize: "16px" }}>
                                            Select GST Types<span style={{ color: "red" }}>*</span>:
                                        </p>
                                    </Grid>
                                    <Grid item sm={9}>
                                        <FormControl fullWidth required>
                                            <Select
                                                size="small"
                                                multiple
                                                value={gstType}
                                                onChange={handleGstTypeChange}
                                                renderValue={(selected) => selected.join(", ")}
                                            >
                                                <MenuItem value="GSTR-1">GSTR-1</MenuItem>
                                                <MenuItem value="GSTR-2A">GSTR-2A</MenuItem>
                                                <MenuItem value="GSTR-2B">GSTR-2B</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </div>

                            <div style={{ padding: "0px 20px 20px 20px" }}>
                                <Grid container spacing={3} alignItems="flex-start">
                                    <Grid item sm={3}>
                                        <p style={{ fontSize: "16px" }}>
                                            From Month and Year<span style={{ color: "red" }}>*</span>:
                                        </p>
                                    </Grid>
                                    <Grid item sm={3}>
                                        <DatePicker
                                            views={['year', 'month']}
                                            placeholder="From Year to Month"
                                            format="MM/yyyy"
                                            value={fromMonthYearDisplay}
                                            onChange={inputHandlerFromPicker}
                                        />
                                    </Grid>
                                    <Grid item sm={3}>
                                        <p style={{ fontSize: "16px" }}>
                                            To Month and Year<span style={{ color: "red" }}>*</span>:
                                        </p>
                                    </Grid>
                                    <Grid item sm={3}>
                                        <DatePicker
                                            views={['year', 'month']}
                                            value={fromToYearDisplay}
                                            format="MM/yyyy"
                                            onChange={inputHandlerToPicker}
                                            minDate={getToMonthDateRange().minDate}
                                            maxDate={getToMonthDateRange().maxDate}
                                            placeholder="Select To Month and Year"
                                            disabled={!fromMonthYearDisplay}
                                        />
                                        {toMonthError && (
                                            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                                {toMonthError}
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                className="button-primary"
                                type="submit"
                                disabled={!!toMonthError}
                                style={{ marginRight: "10px" }}
                            >
                                Submit
                            </button>
                            <button
                                className="button-outlined-secondary"
                                type="button"
                                onClick={handleClose}
                            >
                                Close
                            </button>
                        </Modal.Footer>
                    </ValidatorForm>
                </Modal>
            </div>

            <BoxWrapper
                boxTitle="GST REPOSITORY"
                enableSearchInput={true}
                searchInputPlaceholder="Search..."
                searchInputOnchange={handleSearch}
                enableAddButton={true}
                addButtonLabel="Refresh"
                addButtonHandlerFn={fetchTableData}
            >
                {originalTableData !== null ? (
                    <div
                        style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            overflowX: 'auto',
                            width: '100%',
                        }}
                    >
                        <table className="custome-table">
                            <thead>
                                <tr>{tableHeader()}</tr>
                            </thead>
                            <tbody>
                                {tableDataRows()}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div
                        style={{
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress />
                        <span
                            style={{
                                justifyContent: "center",
                                position: "fixed",
                                top: "55%",
                            }}
                        >
                            Loading...please wait
                        </span>
                    </div>
                )}
            </BoxWrapper>

            <Snackbar
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                >
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
            <Modal
                size="lg"
                aria-labelledby="report-status-modal-title"
                centered
                show={reportStatusModalOpen}
                onHide={closeReportStatusModal}
                dialogClassName="modal-lg"
            >
                <Modal.Header closeButton className="modal-header">
                    <Modal.Title id="report-status-modal-title">
                        Auto Download Status Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="responsive-table">
                        <table className="custome-table">
                            <thead>
                                <tr>
                                    <th>Period</th>
                                    <th>GSTR-1</th>
                                    <th>GSTR-2A</th>
                                    <th>GSTR-2B</th>
                                </tr>
                            </thead>
                            <tbody>
                                {autoDownloadStatusData && Object.keys(autoDownloadStatusData).length > 0 ? (
                                    Object.entries(autoDownloadStatusData).map(([period, reconTypes]) => (
                                        <tr key={period}>
                                            <td style={{ fontWeight: "600", textAlign: "center" }}>
                                                {moment(period, "MMYYYY").isValid()
                                                    ? moment(period, "MMYYYY").format("MMM YYYY")
                                                    : period}
                                            </td>
                                            {["GSTR-1", "GSTR-2A", "GSTR-2B"].map((reconType) => {
                                                const details = reconTypes[reconType];
                                                return (
                                                    <td key={reconType} style={{ textAlign: "center" }}>
                                                        {details ? (
                                                            <div
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => {
                                                                    const message = `${reconType}: ${details.reason || details.status}`;
                                                                    const severity = details.status === "success" ? "success" : "error";
                                                                    handleSnackbarOpen(message, severity);
                                                                }}
                                                            >
                                                                <VisibilityOutlinedIcon
                                                                    className="action-icon-button showIcon"
                                                                    style={{
                                                                        color: details.status === "success" ? "#2938c2" : "#f44336",
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: "center" }}>
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="button-outlined-secondary"
                        onClick={closeReportStatusModal}
                    >
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </Box>
    );
};

export default GSTRepositoryMain;